/**
 * Guarded date formatting helpers.
 *
 * The API serializes datetimes as ISO-8601 with a UTC offset
 * (`2026-06-16T12:00:00+00:00`); native `new Date()` parses that correctly.
 * The failure mode these helpers prevent is the literal string "Invalid Date"
 * appearing in the UI when a value is null, empty, or unparseable — every
 * `new Date(x).toLocaleString()` without a guard renders "Invalid Date" in
 * those cases. Route date rendering through these so a bad value degrades to
 * a fallback ("—") instead.
 */

export type DateInput = string | number | Date | null | undefined;

export interface FormatDateOptions extends Intl.DateTimeFormatOptions {
  /** Locale passed to toLocale*; defaults to the browser locale. */
  locale?: string;
  /** Rendered when the value is missing or unparseable. Defaults to "—". */
  fallback?: string;
}

/** Parse any date input to a valid Date, or null if missing/unparseable. */
export function toValidDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** True when the value parses to a real date. */
export function isValidDate(value: DateInput): boolean {
  return toValidDate(value) !== null;
}

/** Date only (e.g. "6/16/2026"). Returns `fallback` for bad input. */
export function formatDate(value: DateInput, options: FormatDateOptions = {}): string {
  const { locale, fallback = "—", ...intl } = options;
  const d = toValidDate(value);
  return d ? d.toLocaleDateString(locale, intl) : fallback;
}

/** Date + time (e.g. "6/16/2026, 12:00 PM"). Returns `fallback` for bad input. */
export function formatDateTime(value: DateInput, options: FormatDateOptions = {}): string {
  const { locale, fallback = "—", ...intl } = options;
  const d = toValidDate(value);
  return d ? d.toLocaleString(locale, intl) : fallback;
}

/**
 * Relative time ("just now", "5m ago", "3h ago", "2d ago"), falling back to a
 * short date past a week. Returns `fallback` (default null) for bad input.
 */
export function formatRelativeTime(
  value: DateInput,
  fallback: string | null = null,
): string | null {
  const d = toValidDate(value);
  if (!d) return fallback;
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
